import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';

import RXService from '@Services/RxService';

import { HEATMAP_OBSERVABLE_TOPICS, HEATMAP_SPLITTER_CHANGE_STATES } from '@Pages/Heatmap/constants';
import { getResizedColumnWithStructureWidth, getResizedRowWithStructureHeight } from '@Pages/Heatmap/HeatmapTable/resizeUtils';
import DensityMetadataContext from '@Pages/Heatmap/HeatmapTable/contexts/densityMetadataContext';

import ColumnsHeader from '@Pages/Heatmap/HeatmapTable/Header/ColumnsHeader';
import RowsHeader from '@Pages/Heatmap/HeatmapTable/Header/RowsHeader';
import CommonHeader from '@Pages/Heatmap/HeatmapTable/Header/CommonHeader';
import Matrix from '@Pages/Heatmap/HeatmapTable/Matrix/Matrix';
import HorizontalSplitter from '@Pages/Heatmap/HeatmapTable/Splitter/HorizontalSplitter';
import VerticalSplitter from '@Pages/Heatmap/HeatmapTable/Splitter/VerticalSplitter';

function Main({ heatmapData, settings, onInitData, onRequestData, onSelectionChange }) {
  const matrixRef = useRef();

  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [hoveredRow, setHoveredRow] = useState({});
  const [hoveredColumn, setHoveredColumn] = useState({});
  const [viewOptions, setViewOptions] = useState(heatmapData.viewOptions);

  const isRowStructure = viewOptions.showStructure && settings.row.value === viewOptions.substanceType;
  const isColStructure = viewOptions.showStructure && settings.col.value === viewOptions.substanceType;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedResizeHandler = useCallback(
    debounce(() => RXService.next(HEATMAP_OBSERVABLE_TOPICS.resizeWindow), viewOptions.resizeDebounce),
    [viewOptions.resizeDebounce]
  );

  const adjustCellSize = useCallback(() => {
    const calcCellWidth = Math.ceil(matrixRef.current.clientWidth / (viewOptions.colsAmount || 1));
    const nextCellWidth = Math.max(calcCellWidth, viewOptions.boundaries.minCellWidth);
    setViewOptions({
      ...viewOptions,
      cellWidth: nextCellWidth,
    });
  }, [viewOptions]);

  const horizontalSplitterChanged = useCallback(
    (state, stateParams) => {
      if (state === HEATMAP_SPLITTER_CHANGE_STATES.released && stateParams.heightDelta) {
        const colsHeaderHeight = viewOptions.colsHeaderHeight + stateParams.heightDelta;
        const cellWidth = isColStructure
          ? getResizedColumnWithStructureWidth(colsHeaderHeight, viewOptions.cellWidth)
          : viewOptions.cellWidth;

        setViewOptions({
          ...viewOptions,
          colsHeaderHeight,
          cellWidth,
        });
      }
    },
    [isColStructure, viewOptions]
  );

  const verticalSplitterChanged = useCallback(
    (state, stateParams) => {
      if (state === HEATMAP_SPLITTER_CHANGE_STATES.released && stateParams.widthDelta) {
        const rowsHeaderWidth = viewOptions.rowsHeaderWidth + stateParams.widthDelta;
        const cellHeight = isRowStructure ? getResizedRowWithStructureHeight(rowsHeaderWidth) : viewOptions.cellHeight;

        setViewOptions({
          ...viewOptions,
          rowsHeaderWidth,
          cellHeight,
        });
      }
    },
    [isRowStructure, viewOptions]
  );

  const handleSelectColumn = useCallback(
    (index, alterSelection) => {
      const _selectedColumns = [...selectedColumns];
      const selectedIndex = _selectedColumns.indexOf(index);
      if (selectedIndex !== -1) {
        if (alterSelection) {
          _selectedColumns.splice(selectedIndex, 1);
        }
      } else {
        _selectedColumns.push(index);
      }
      setSelectedColumns(_selectedColumns);
      setSelectedRows([]);
    },
    [selectedColumns]
  );

  const handleSelectRow = useCallback(
    (index, alterSelection) => {
      const _selectedRows = [...selectedRows];
      const selectedIndex = _selectedRows.indexOf(index);
      if (selectedIndex !== -1) {
        if (alterSelection) {
          _selectedRows.splice(selectedIndex, 1);
        }
      } else {
        _selectedRows.push(index);
      }
      setSelectedRows(_selectedRows);
      setSelectedColumns([]);
    },
    [selectedRows]
  );

  const handleHeaderHover = useCallback(({ index, isRow, showBorder }) => {
    setHoveredColumn(isRow ? {} : { index, showBorder });
    setHoveredRow(isRow ? { index, showBorder } : {});
  }, []);

  const handleCellHover = useCallback(({ column, row }) => {
    setHoveredColumn({ index: column, showBorder: false });
    setHoveredRow({ index: row, showBorder: false });
  }, []);

  useEffect(() => {
    adjustCellSize();
    window.addEventListener('resize', debouncedResizeHandler, false);
    return () => {
      window.removeEventListener('resize', debouncedResizeHandler, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const rxSubscriptions = [RXService.subscribe(HEATMAP_OBSERVABLE_TOPICS.resizeWindow, adjustCellSize)];
    return () => {
      rxSubscriptions.forEach((rxSubscription) => rxSubscription.unsubscribe());
    };
  }, [adjustCellSize]);

  useEffect(() => {
    const rxSubscriptions = [RXService.subscribe(HEATMAP_OBSERVABLE_TOPICS.horizontalSplitter, horizontalSplitterChanged)];
    return () => {
      rxSubscriptions.forEach((rxSubscription) => rxSubscription.unsubscribe());
    };
  }, [horizontalSplitterChanged]);

  useEffect(() => {
    const rxSubscriptions = [RXService.subscribe(HEATMAP_OBSERVABLE_TOPICS.verticalSplitter, verticalSplitterChanged)];
    return () => {
      rxSubscriptions.forEach((rxSubscription) => rxSubscription.unsubscribe());
    };
  }, [verticalSplitterChanged]);

  useEffect(() => {
    const rxSubscriptions = [RXService.subscribe(HEATMAP_OBSERVABLE_TOPICS.resizeHeader, debouncedResizeHandler)];
    return () => {
      rxSubscriptions.forEach((rxSubscription) => rxSubscription.unsubscribe());
    };
  }, [debouncedResizeHandler]);

  useEffect(() => {
    RXService.next(HEATMAP_OBSERVABLE_TOPICS.resizeHeader);
  }, [viewOptions.cellWidth, viewOptions.cellHeight, viewOptions.rowsHeaderWidth, viewOptions.colsHeaderHeight]);

  useEffect(() => {
    onSelectionChange({
      selectedRows,
      selectedColumns,
      totalRows: viewOptions.rowsAmount,
      totalColumns: viewOptions.colsAmount,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows, selectedColumns]);

  const densityMetadata = useMemo(
    () => ({
      rowsMaxDensity: heatmapData.rowsMaxDensity,
      colsMaxDensity: heatmapData.colsMaxDensity,
      maxDensityDiameter: viewOptions.boundaries.maxDensityDiameter,
      minDensityDiameter: viewOptions.boundaries.minDensityDiameter,
    }),
    [
      heatmapData.rowsMaxDensity,
      heatmapData.colsMaxDensity,
      viewOptions.boundaries.maxDensityDiameter,
      viewOptions.boundaries.minDensityDiameter,
    ]
  );

  return (
    <div className="heatmap">
      <DensityMetadataContext.Provider value={densityMetadata}>
        <ColumnsHeader
          viewOptions={viewOptions}
          items={heatmapData.headers.cols}
          showStructure={isColStructure}
          loaded={Boolean(heatmapData.colsLoaded)}
          selectHeaderItem={handleSelectColumn}
          handleHover={handleHeaderHover}
          selectedHeaderItems={selectedColumns}
          hoveredItem={hoveredColumn}
        />
        <RowsHeader
          viewOptions={viewOptions}
          items={heatmapData.headers.rows}
          showStructure={isRowStructure}
          loaded={Boolean(heatmapData.rowsLoaded)}
          selectHeaderItem={handleSelectRow}
          handleHover={handleHeaderHover}
          selectedHeaderItems={selectedRows}
          hoveredItem={hoveredRow}
        />
      </DensityMetadataContext.Provider>
      <CommonHeader viewOptions={viewOptions} settings={settings} />
      <Matrix
        ref={matrixRef}
        values={heatmapData.data}
        viewOptions={viewOptions}
        onInitData={onInitData}
        onRequestData={onRequestData}
        loaded={Boolean(heatmapData.matrixLoaded)}
        selectedRows={selectedRows}
        selectedColumns={selectedColumns}
        handleHover={handleCellHover}
        hoveredColumn={hoveredColumn}
        hoveredRow={hoveredRow}
      />
      <HorizontalSplitter showStructure={isColStructure} />
      <VerticalSplitter showStructure={isRowStructure} />
    </div>
  );
}

Main.propTypes = {
  heatmapData: PropTypes.object.isRequired,
  settings: PropTypes.object.isRequired,
  onInitData: PropTypes.func,
  onRequestData: PropTypes.func,
  onSelectionChange: PropTypes.func,
};

export default Main;
